import React, { useEffect, useState, useCallback } from 'react';
import { QRCodeCanvas } from "qrcode.react"; 
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { useSnackbar } from 'notistack';
import { CircularProgress } from '@mui/material';
import { useStore } from '../../../stores/store';
import { useSetMenu } from "../../../hooks/useSetMenu";

const QRCodeAttendance = () => {
  const [qrUrl, setQrUrl] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [schedulePlanWeekId, setSchedulePlanWeekId] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [attendeeCount, setAttendeeCount] = useState(0);
  const [expectedStudents, setExpectedStudents] = useState(0);
  const [startScanCount, setStartScanCount] = useState(0);
  const [endScanCount, setEndScanCount] = useState(0);
  const [timeTillExpiry, setTimeTillExpiry] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const [sessionType, setSessionType] = useState(1); // 1 = Start, 2 = End
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const token = query.get('token');
  const sessionIdParam = query.get('sessionId');
  const schedulePlanWeekIdParam = query.get('schedulePlanWeekId');
  const sessionTypeParam = query.get('sessionType');
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const { mainStore } = useStore();
  useSetMenu(mainStore.SchedulePlanMenuType, mainStore.SchedulePlanMenuNo);

  // Extract token, sessionId, schedulePlanWeekId, and sessionType from query parameters.
  useEffect(() => {
    if (token) {
      const baseUrl = window.__UMS_THEME__?.frontUrl || process.env.REACT_APP_FRONT_URL;
      const url = `${baseUrl}/attendance/markwithtoken?token=${token}`;
      setQrUrl(url);
    }
    if (sessionIdParam) {
      setSessionId(sessionIdParam);
    }
    if (schedulePlanWeekIdParam) {
      setSchedulePlanWeekId(schedulePlanWeekIdParam);
    }
    if (sessionTypeParam) {
      setSessionType(parseInt(sessionTypeParam) || 1);
    }
  }, [token, sessionIdParam, schedulePlanWeekIdParam, sessionTypeParam]);

  // Refresh attendance count at regular intervals
  const refreshAttendanceCount = useCallback(async () => {
    if (!sessionId) return;
    
    try {
      // First, check if the session is still active
      const sessionResponse = await axiosPrivate.get(`/SchedulePlanWeekSessions/ActiveSession/${schedulePlanWeekId}`);
      const sessionData = sessionResponse.data;
      
      if (sessionData && sessionData.tokenExpiry) {
        const expiryTime = new Date(sessionData.tokenExpiry);
        const now = new Date();
        const timeLeft = expiryTime - now;
        
        if (timeLeft <= 0) {
          setIsExpired(true);
          setTimeTillExpiry('Expired');
        } else {
          setIsExpired(false);
          // Format time left as mm:ss
          const minutes = Math.floor(timeLeft / 60000);
          const seconds = Math.floor((timeLeft % 60000) / 1000);
          setTimeTillExpiry(`${minutes}:${seconds < 10 ? '0' : ''}${seconds}`);
        }
      }
      
      // Get attendance count
      const statsResponse = await axiosPrivate.get(`/SchedulePlanWeekSessions/GetAttendanceStatistics/${schedulePlanWeekId}`);
      if (statsResponse.data) {
        setAttendeeCount(statsResponse.data.present);
        setExpectedStudents(statsResponse.data.totalEnrolled);
        setStartScanCount(statsResponse.data.startScanCount || 0);
        setEndScanCount(statsResponse.data.endScanCount || 0);
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  }, [sessionId, schedulePlanWeekId, axiosPrivate]);

  useEffect(() => {
    if (sessionId && schedulePlanWeekId) {
      refreshAttendanceCount();
      const interval = setInterval(refreshAttendanceCount, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [sessionId, schedulePlanWeekId, refreshAttendanceCount]);

  // Function to handle session recreation
  // NOTE: Coordinates are now automatically retrieved from the Room's BranchObject on the backend
  const handleRecreateSession = async () => {
    try {
      const response = await axiosPrivate.post(`/SchedulePlanWeekSessions/StartSession/${schedulePlanWeekId}`, {
        ForceNew: true,
        SessionType: sessionType
      });

      if (response.data && response.data.token) {
        // Navigate to the correct QR page with new token
        navigate(`/attendance/qr?token=${response.data.token}&sessionId=${response.data.sessionId}&schedulePlanWeekId=${schedulePlanWeekId}&sessionType=${sessionType}`);
        enqueueSnackbar(t("sessionRecreated"), { variant: "success" });
      }
    } catch (error) {
      console.error("Error recreating session:", error);
      enqueueSnackbar(t("errorRecreatingSession"), { variant: "error" });
    }
  };

  // Function to handle session cancellation
  const handleCancelSession = async () => {
    try {
      await axiosPrivate.delete(`/SchedulePlanWeekSessions/CancelSession/${sessionId}`);
      enqueueSnackbar(t("sessionCanceled"), { variant: "success" });
      navigate(-1); // Go back to previous page
    } catch (error) {
      console.error("Error canceling session:", error);
      enqueueSnackbar(t("errorCancelingSession"), { variant: "error" });
    }
  };

  // Function to view attendance list
  const handleViewAttendance = () => {
    navigate(`/attendance/studentlist?schedulePlanWeekId=${schedulePlanWeekId}`);
  };

  // Calculate attendance rate based on session type
  // For Start session: show start scan count
  // For End session: show end scan count
  const currentScanCount = sessionType === 1 ? startScanCount : endScanCount;
  const attendanceRate = expectedStudents > 0 ? (currentScanCount / expectedStudents) * 100 : 0;

  // Determine header styling based on session type
  const getHeaderClass = () => {
    return 'bg-danger'; // Use red color to match the portal theme
  };

  const getSessionTypeLabel = () => {
    return sessionType === 1 ? t("scanQRStartSession") : t("scanQREndSession");
  };

  return (
    <div className="container my-4">
      <div className="card">
        <div className={`card-header ${getHeaderClass()} text-white d-flex justify-content-between align-items-center`}>
          <div>
            <h3 className="mb-0">{getSessionTypeLabel()}</h3>
            <small className="opacity-75">
              {sessionType === 1 ? t("beginningOfLecture") : t("endOfLecture")}
            </small>
          </div>
          <button 
            className="btn btn-light" 
            onClick={() => navigate(-1)}
            title={t("backToList")}
          >
            <i className="bi bi-arrow-left"></i> {t("backToList")}
          </button>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-8 text-center">
              {qrUrl ? (
                <div className="mb-4">
                  <QRCodeCanvas
                    value={qrUrl}
                    size={600}
                    bgColor={"#ffffff"}
                    fgColor={"#000000"}
                    level={"H"}
                    className="border p-2 rounded"
                  />
                  {isExpired ? (
                    <div className="alert alert-danger mt-3">
                      {t("sessionExpiredMessage")}
                    </div>
                  ) : (
                    <div className="mt-3">
                      <p className="text-muted">
                        {t("sessionExpiresIn")}: <strong>{timeTillExpiry}</strong>
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <CircularProgress sx={{ color: 'var(--brand-red, #dc354d)' }} />
              )}
            </div>
            <div className="col-md-4">
              <div className="card h-100">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                  <h4 className="mb-0">{t("attendanceStatistics")}</h4>
                  <span className="badge bg-danger">
                    {sessionType === 1 ? t("startSession") : t("endSession")}
                  </span>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-2">
                    <span>{t("totalEnrolled")}:</span>
                    <strong>{expectedStudents}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>{sessionType === 1 ? t("startScan") : t("endScan")}:</span>
                    <strong className="text-danger">{currentScanCount}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>{t("attendanceRate")}:</span>
                    <strong>{attendanceRate.toFixed(1)}%</strong>
                  </div>

                  <div className="progress mb-3" style={{ height: '20px' }}>
                    <div
                      className={`progress-bar ${attendanceRate > 50 ? 'bg-success' : 'bg-warning'}`}
                      role="progressbar"
                      style={{ width: `${attendanceRate}%` }}
                      aria-valuenow={attendanceRate}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {attendanceRate.toFixed(1)}%
                    </div>
                  </div>

                  {/* Show both scan counts for context */}
                  <hr />
                  <small className="text-muted d-block mb-1">
                    {t("startScan")}: <strong>{startScanCount}</strong>
                  </small>
                  <small className="text-muted d-block">
                    {t("endScan")}: <strong>{endScanCount}</strong>
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card-footer">
          <div className="d-flex justify-content-between">
            <button 
              className="btn btn-danger" 
              onClick={handleCancelSession}
            >
              {t("cancelSession")}
            </button>
            <div>
              <button
                className="btn btn-outline-danger me-2"
                onClick={handleViewAttendance}
              >
                {t("viewAttendance")}
              </button>
              {isExpired && (
                <button
                  className="btn btn-danger"
                  onClick={handleRecreateSession}
                >
                  {t("recreateSession")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeAttendance;
